import React from 'react';
import { Paper, Typography, Box, Grid, useTheme } from '@mui/material';
import SettingFieldRenderer from './SettingFieldRenderer';

// Field types that benefit from spanning the full row width on desktop
// (they have their own internal layout or are visually large).
const FULL_WIDTH_TYPES = new Set(['image', 'textarea']);

// Fully-qualified field keys (`${sectionKey}.${fieldKey}`) that must stay
// read-only in the UI. Currency switching is currently locked because
// changing it after orders exist requires a manual data migration.
const LOCKED_FIELD_KEYS = new Set(['currency.default']);

// Detect the renderer type the same way SettingFieldRenderer does, so we
// know which grid span to give each field. Mirror the inference rules
// without importing the helpers (they're component-local).
const SECRET_KEY_HINTS = ['secret', 'password', 'api_key', 'apikey', 'token', 'private_key'];
const isBilingual = (val) =>
    val !== null &&
    typeof val === 'object' &&
    !Array.isArray(val) &&
    ('ar' in val || 'en' in val);
const looksLikeImageUrl = (val) =>
    typeof val === 'string' &&
    /^https?:\/\//.test(val) &&
    /\.(png|jpe?g|svg|webp|gif|avif)(\?|$)/i.test(val);
const looksSensitive = (key) => SECRET_KEY_HINTS.some((h) => key.toLowerCase().includes(h));
const IMAGE_FIELD_KEYS = new Set(['image', 'logo', 'banner_image', 'header_image', 'icon']);
const looksLikeImageKey = (key) => IMAGE_FIELD_KEYS.has(key) || /_(image|logo|icon|banner)$/i.test(key);
const inferFieldType = (key, value) => {
    if (typeof value === 'boolean') return 'boolean';
    if (isBilingual(value)) return 'bilingual';
    if (typeof value === 'number') return 'number';
    if (looksSensitive(key)) return 'secret';
    if (looksLikeImageUrl(value)) return 'image';
    if (looksLikeImageKey(key) && typeof value === 'string') return 'image';
    if (typeof value === 'string' && value.length > 80) return 'textarea';
    return 'text';
};

const SettingsSectionCard = ({
    title,
    description,
    sectionKey,
    sectionData,
    onChange,
    fieldOrder,
    disabled,
    activeLang,
}) => {
    const theme = useTheme();
    const data = sectionData && typeof sectionData === 'object' ? sectionData : {};
    const keys = fieldOrder
        ? fieldOrder.filter((k) => k in data || data[k] !== undefined)
        : Object.keys(data);

    const handleFieldChange = (fieldKey) => (value) => {
        onChange(sectionKey, { ...data, [fieldKey]: value });
    };

    if (keys.length === 0) {
        return null;
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                mb: 3,
            }}
        >
            <Box mb={2}>
                <Typography variant="h6" fontWeight={700}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {description}
                    </Typography>
                )}
            </Box>

            <Grid container spacing={2.5}>
                {keys.map((fieldKey) => {
                    const inferred = inferFieldType(fieldKey, data[fieldKey]);
                    // Bilingual fields without an activeLang context render
                    // two side-by-side inputs themselves, so they need the
                    // full row width too.
                    const isFullWidth =
                        FULL_WIDTH_TYPES.has(inferred) ||
                        (inferred === 'bilingual' && !activeLang);
                    const isLocked = LOCKED_FIELD_KEYS.has(`${sectionKey}.${fieldKey}`);
                    return (
                        <Grid
                            item
                            xs={12}
                            md={isFullWidth ? 12 : 6}
                            key={fieldKey}
                        >
                            <SettingFieldRenderer
                                fieldKey={fieldKey}
                                value={data[fieldKey]}
                                onChange={handleFieldChange(fieldKey)}
                                disabled={disabled || isLocked}
                                activeLang={activeLang}
                                type={inferred}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </Paper>
    );
};

export default SettingsSectionCard;
